/**
 * Consent runtime — the single source of truth for whether Google Analytics
 * (and, separately, Google signals / advertising measurement) may run on
 * this browser. Imported by both SiteAnalytics (which starts gtag) and
 * CookieNotice (the banner that asks). See backlog/details/BL-0079/plan.md
 * sections 3.1-3.5 for the design this implements; do not re-derive it here.
 *
 * Two purposes, independently toggleable, because they are different things
 * legally (measurement vs. advertising) and technically (four gtag consent
 * signals):
 *   - analytics -> analytics_storage -> GA4 cookies (_ga, _ga_<ID>) + events
 *   - ads       -> ad_storage, ad_user_data, ad_personalization -> Google
 *                  signals now, Google Ads measurement/remarketing later
 *
 * A stored choice (the pp_consent cookie) always wins over the region
 * default. Global Privacy Control only narrows the ADVERTISING default in
 * the opt-out region; an explicit "Accept all" or a saved Manage choice
 * overrides it, same as the CPRA regulations allow.
 *
 * Nothing here talks to React. CookieNotice/PrivacyNotice subscribe() to be
 * told when the state changes (a choice was made, the panel opened/closed).
 */

/** Bump when the notice's purposes change materially — re-prompts everyone. */
export const CONSENT_VERSION = 1;

/** 'regional': opt-in in the EEA/UK/Switzerland, opt-out elsewhere (the
 *  chosen posture, section 3.1/8.3). 'optin-all' would ask everyone first;
 *  kept as a single constant so it is a one-line change if that changes. */
export const CONSENT_MODE = 'regional';

/** Rendered on the notice page as "Last updated". Bump alongside real edits. */
export const NOTICE_UPDATED = '2026-08-29';

const COOKIE_NAME = 'pp_consent';
const NOTICE_COOKIE = 'pp_notice';

/**
 * Timezones outside the `Europe/` prefix that still name an EEA/UK/CH
 * territory (French/Danish/Norwegian/Spanish overseas zones, Cyprus via
 * `Asia/`, Svalbard, Ceuta). Deliberately over-inclusive: erring toward
 * opt-in is the legally safe direction (section 3.3).
 */
const EU_EXTRA_TIMEZONES = [
  'Atlantic/Reykjavik',
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Atlantic/Faroe',
  'Atlantic/Faeroe',
  'Atlantic/Jan_Mayen',
  'Arctic/Longyearbyen',
  'Africa/Ceuta',
  'Asia/Nicosia',
  'Asia/Famagusta',
  'America/Cayenne',
  'America/Guadeloupe',
  'America/Martinique',
  'America/Marigot',
  'America/St_Barthelemy',
  'Indian/Reunion',
  'Indian/Mayotte',
  'America/Nuuk',
  'America/Godthab',
  'America/Danmarkshavn',
  'America/Scoresbysund',
  'America/Thule',
];

/** ISO region subtags treated as EEA/UK/CH for the `navigator.languages`
 *  check. `GB`/`en-GB` included deliberately (over-inclusive by design). */
const EU_LANGUAGE_REGIONS = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'CH',
];

const isEuTimeZone = (timeZone) =>
  !!timeZone &&
  (timeZone.startsWith('Europe/') || EU_EXTRA_TIMEZONES.includes(timeZone));

const hasEuLanguageRegion = (languages) =>
  Array.isArray(languages) &&
  languages.some((lang) => {
    const m = /-([A-Za-z]{2})$/.exec(lang || '');
    return !!m && EU_LANGUAGE_REGIONS.includes(m[1].toUpperCase());
  });

/**
 * The ALB sends no country header and the edge box has no GeoIP module, so
 * this is the only signal available. Missing timeZone/Intl and the
 * `?pp_region=optin` test flag both resolve to opt-in — the safe direction.
 * There is deliberately NO `optout` value: the flag can only make the site
 * stricter (section 3.3).
 */
export function isOptInRegion({ timeZone, languages, forceOptIn } = {}) {
  if (forceOptIn) return true;
  if (!timeZone) return true;
  if (isEuTimeZone(timeZone)) return true;
  if (hasEuLanguageRegion(languages)) return true;
  return false;
}

/**
 * `pp_consent` cookie value: `v<version>.a<0|1>.p<0|1>.<unix seconds>`
 * (section 3.4). Returns null for an absent cookie AND for a version
 * mismatch — a version bump must re-prompt, not half-trust old data.
 */
export function parseConsent(cookieString) {
  if (typeof cookieString !== 'string') return null;
  const found = /(?:^|;\s*)pp_consent=([^;]*)/.exec(cookieString);
  if (!found) return null;
  let raw;
  try {
    raw = decodeURIComponent(found[1]);
  } catch (e) {
    return null;
  }
  const m = /^v(\d+)\.a([01])\.p([01])\.(\d+)$/.exec(raw);
  if (!m) return null;
  const v = Number(m[1]);
  if (v !== CONSENT_VERSION) return null;
  return { v, a: m[2] === '1', p: m[3] === '1', ts: Number(m[4]) };
}

export function serializeConsent({ a, p }, now) {
  const ts = Number.isFinite(now) ? Math.floor(now) : 0;
  return `v${CONSENT_VERSION}.a${a ? 1 : 0}.p${p ? 1 : 0}.${ts}`;
}

/**
 * Region x stored-choice x GPC -> what actually applies. Stored ALWAYS wins
 * (a saved choice, including a saved "off", is more specific than any
 * default). With no stored choice: 'optin-all' denies everyone; 'regional'
 * denies in the opt-in region and grants analytics elsewhere, narrowing
 * advertising to denied when GPC is present (section 3.1).
 */
export function effectiveConsent({ stored, optIn, gpc, mode }) {
  if (stored) {
    return { analytics: !!stored.a, ads: !!stored.p };
  }
  if (mode === 'optin-all' || optIn) {
    return { analytics: false, ads: false };
  }
  return { analytics: true, ads: !gpc };
}

export function toGtagConsent({ analytics, ads }) {
  const g = (v) => (v ? 'granted' : 'denied');
  return {
    analytics_storage: g(analytics),
    ad_storage: g(ads),
    ad_user_data: g(ads),
    ad_personalization: g(ads),
  };
}

// ---------------------------------------------------------------------------
// DOM layer
// ---------------------------------------------------------------------------
//
// BL-0135 note: this file does NOT send an explicit page_view on client-side
// route changes, and should not gain one back without new evidence. GA4's
// "Page changes based on browser history events" enhanced-measurement
// setting is already on for this stream and already sends one on every
// client-side navigation with zero code in this bundle -- confirmed live
// against the pre-fix build: a click from / to /glossary on
// archives.directives.doxcelerate.com produced
// `/g/collect ... en=page_view&dl=https://archives.directives.doxcelerate.com/glossary
// &dr=https://archives.directives.doxcelerate.com/&_s=3`
// [Chrome DevTools, pre-fix build, 2026-09-27]. GA4's own aggregate numbers
// agree with more than one page view per session, which a landing-page-only
// hit could never produce: 1.59 page views/session on directives, 1.97 on
// nnsa, 1.38 on standards, trailing 14 days [ga4 report hostName x
// screenPageViewsPerSession, 2026-09-27]. An earlier revision of this file
// added a manual trackPageView()/send_page_view:false pair on the theory
// that route changes were not measured at all; that theory did not survive
// the observation above and the pair was removed -- it would have
// double-counted every route change against enhanced measurement's own hit.
// The evidenced defect this file actually fixes is prerendering, below.

export function readConsent() {
  return typeof document === 'undefined' ? null : parseConsent(document.cookie);
}

/**
 * Sets the cookie exactly per section 3.4: 12-month Max-Age, host-wide on
 * doxcelerate.com (so one choice covers all three archives), Secure only
 * over https (so it still round-trips on plain-http local dev).
 */
const setSiteCookie = (name, value) => {
  if (typeof document === 'undefined') return;
  const hostname =
    typeof window !== 'undefined' && window.location.hostname
      ? window.location.hostname
      : '';
  const parts = [
    `${name}=${value}`,
    'Max-Age=31536000',
    'Path=/',
    'SameSite=Lax',
  ];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }
  if (hostname.endsWith('.doxcelerate.com')) {
    parts.push('Domain=.doxcelerate.com');
  }
  document.cookie = parts.join('; ');
};

export function writeConsent({ a, p }) {
  if (typeof document === 'undefined') return;
  const now = Math.floor(Date.now() / 1000);
  setSiteCookie(COOKIE_NAME, serializeConsent({ a, p }, now));
}

/**
 * `pp_notice` — "this browser has already been shown the banner once".
 *
 * Only the OPT-OUT region is allowed to act on it. No banner is legally
 * required there at all (CCPA/CPRA ask for a notice-at-collection link and a
 * Do Not Sell or Share link, both of which live permanently in the footer),
 * so one showing is already more than the minimum, and repeating it forever
 * buys nothing for a visitor whose analytics is on by default either way.
 *
 * In the opt-in region it is written but must never hide anything: that
 * banner stays until there is an actual decision, because silence is not
 * consent (GDPR recital 32, ePrivacy art. 5(3)). The gate is in the banner,
 * on `optIn` — not here.
 */
export function readNoticeSeen() {
  if (typeof document === 'undefined') return false;
  const m = /(?:^|;\s*)pp_notice=([^;]*)/.exec(document.cookie);
  return !!m && m[1] === '1';
}

/**
 * Expires every GA cookie actually present rather than guessing one
 * measurement id, so it also catches `_ga_<ID>` for whichever site's id is
 * live, `_gid`, and `_gat*`. Cleared on the host-only cookie, the shared
 * doxcelerate.com domain, and the current hostname's own dot-domain, since a
 * cookie set on any of those three shapes is invisible to a delete on the
 * wrong one.
 */
export function clearGaCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((part) => part.trim().split('=')[0])
    .filter(Boolean)
    .filter(
      (name) =>
        /^_ga(_|$)/.test(name) || /^_gid$/.test(name) || /^_gat/.test(name),
    );
  if (!names.length) return;
  const hostname =
    typeof window !== 'undefined' && window.location.hostname
      ? window.location.hostname
      : '';
  const domainSuffixes = ['', '; domain=.doxcelerate.com'];
  if (hostname) domainSuffixes.push(`; domain=.${hostname}`);
  names.forEach((name) => {
    domainSuffixes.forEach((suffix) => {
      document.cookie = `${name}=; Max-Age=0; path=/${suffix}`;
    });
  });
}

export function detectGpc() {
  return (
    typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true
  );
}

const getRegionInputs = () => {
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    timeZone = undefined;
  }
  let languages = [];
  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages) && navigator.languages.length) {
      languages = navigator.languages;
    } else if (navigator.language) {
      languages = [navigator.language];
    }
  }
  const forceOptIn =
    typeof window !== 'undefined' &&
    /[?&]pp_region=optin(?:&|$)/.test(window.location.search || '');
  return { timeZone, languages, forceOptIn };
};

// Module-level state. One page load, one consent runtime — a second
// initAnalytics call (there is only ever one SiteAnalytics per page, but
// tests exercise this directly) must not double-start gtag or double-fire
// listeners with half-updated state.
let gtagStarted = false;
let currentMeasurementId = null;
let state = {
  stored: null,
  optIn: false,
  gpc: false,
  effective: { analytics: false, ads: false },
  open: false,
  noticeSeen: false,
};
const listeners = new Set();

const recalcOnly = () => {
  const stored = readConsent();
  const { timeZone, languages, forceOptIn } = getRegionInputs();
  const optIn = isOptInRegion({ timeZone, languages, forceOptIn });
  const gpc = detectGpc();
  const effective = effectiveConsent({
    stored,
    optIn,
    gpc,
    mode: CONSENT_MODE,
  });
  state = {
    ...state,
    stored,
    optIn,
    gpc,
    effective,
    noticeSeen: readNoticeSeen(),
  };
};

export function getState() {
  return { ...state, gtagLoaded: gtagStarted };
}

const notify = () => {
  const snapshot = getState();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (e) {
      // A broken subscriber must never take consent state down with it.
    }
  });
};

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Records that the banner has been shown. Deliberately does NOT notify:
 * the banner is the only reader, it decides at mount from the value it found
 * on arrival, and a re-render here would hide the very showing that set the
 * cookie. It also never touches consent — a visitor who ignores the notice
 * has made no choice, and `effectiveConsent` must keep returning the region
 * default rather than a decision they never took.
 */
export function markNoticeSeen() {
  if (typeof document === 'undefined' || state.noticeSeen) return;
  setSiteCookie(NOTICE_COOKIE, '1');
  state = { ...state, noticeSeen: true };
}

export function openPanel() {
  state = { ...state, open: true };
  notify();
}

export function closePanel() {
  state = { ...state, open: false };
  notify();
}

/**
 * Defines window.dataLayer/gtag if absent, pushes 'consent' 'default' BEFORE
 * 'config' (load-bearing order — a config call before the default consent
 * push would ship the first hit with no consent state at all), and appends
 * the gtag.js loader exactly once per page load. A second call is a no-op:
 * the module-level flag is checked, not `window.gtag`'s existence, because
 * the stub above can exist before the loader script has been appended.
 */
export function startGtag(measurementId, consent) {
  if (typeof window === 'undefined' || !measurementId) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  if (gtagStarted) return;
  gtagStarted = true;
  window.gtag('consent', 'default', toGtagConsent(consent));
  if (!consent.ads) {
    // Redact ad-click identifiers from GA4's own hit even though ad_storage
    // is already denied — belt and braces for O2 (Google's Advertising
    // Features policy requirements).
    window.gtag('set', { ads_data_redaction: true });
  }
  window.gtag('js', new Date());
  // 2026-09-09 (TAIUIT item 7). gtag.js writes _ga and _ga_<id>
  // itself, so setSiteCookie's flags never reached them and they
  // shipped with no Secure and no SameSite. cookie_flags is the only
  // lever gtag exposes for it. Lax, not Strict: the cookie has to
  // survive a cross-site navigation INTO the archive or the session
  // is attributed to the wrong source, which is what it is for.
  //
  // send_page_view is left at its default (true) deliberately (BL-0135):
  // this config call's own automatic page_view IS the landing-page hit, and
  // every later client-side route change is separately covered by GA4's
  // "Page changes based on browser history events" enhanced measurement,
  // already on for this stream (see the DOM layer note above) -- there is
  // nothing left for this file to send by hand.
  window.gtag('config', measurementId, {
    cookie_flags: 'secure;samesite=lax',
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

/**
 * Called once by SiteAnalytics on mount. Preserves today's pp_dev behaviour
 * byte-for-byte in effect (?pp_dev=1 sets a year-long cookie and disables
 * analytics for that browser; ?pp_dev=0 clears it; the cookie's mere
 * presence means no gtag at all, window.gtag stays undefined) — that guard
 * runs BEFORE any consent decision, so a diagnostic visit never becomes a
 * counted one regardless of region.
 *
 * Otherwise: in the opt-in region with no stored choice, nothing is
 * requested from Google at all (section 3.2's strict posture) — no loader,
 * no dataLayer. Everywhere else, or once a stored choice grants something,
 * startGtag runs immediately with the consent state that applies right now.
 *
 * Prerendering (BL-0135) is the actual fix here — the rest of this file's
 * page_view behaviour is unchanged from before BL-0135, once it turned out
 * route changes did not need separate handling (see the DOM layer note
 * above: enhanced measurement already covers them, live-confirmed against
 * the pre-fix build). Chromium's Speculation Rules API can render this page
 * in the background before anyone actually visits it — documented, common
 * behaviour for Bing search results opened in Edge, and the strongest
 * correlate found in the BL-0135 data (205/240 of directives' page_view-less
 * sessions were Edge, 173/240 bing/organic [ga4 report
 * landingPage,sessionSource,sessionBrowser --site directives, 2026-09-27]).
 * A prerendered document that is never activated must never be counted as a
 * visit; one that is activated must be counted as of the moment it
 * activates, not before. Deferring everything — including the pp_dev cookie
 * handling below — until `prerenderingchange` fires is stronger than relying
 * on gtag.js's own internal hit-deferral, which only defers sending hits,
 * not loading the enhanced-measurement listeners that this codebase never
 * gated on visibility at all (grep for "prerender" across the addon and
 * Volto core returns nothing prior to this change) — and matches the
 * BL-0135 evidence shape (session_start + user_engagement + scroll present,
 * page_view absent) if any of those listeners fired during a prerender that
 * never activated.
 */
export function initAnalytics(measurementId) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (document.prerendering) {
    document.addEventListener(
      'prerenderingchange',
      () => initAnalytics(measurementId),
      { once: true },
    );
    return;
  }
  currentMeasurementId = measurementId || null;

  const ppDev = /[?&]pp_dev=([^&]*)/.exec(window.location.search || '');
  if (ppDev) {
    // Same audit item. This one is ours, and it was the only cookie
    // the app writes that setSiteCookie does not. Secure is applied
    // only over https so that an http origin can still clear it.
    const devSecure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      ppDev[1] === '0'
        ? 'pp_dev=; Max-Age=0; path=/; SameSite=Lax'
        : `pp_dev=1; Max-Age=31536000; path=/; SameSite=Lax${devSecure}`;
  }
  if (/(?:^|; )pp_dev=/.test(document.cookie)) {
    return;
  }
  if (!currentMeasurementId) return;

  recalcOnly();
  if (state.effective.analytics || state.effective.ads) {
    startGtag(currentMeasurementId, state.effective);
  }
  notify();
}

/**
 * The banner's Accept all / Decline / Manage all funnel through here.
 * Writes the cookie (source of truth from the next page load on), updates
 * the live gtag consent signals if the loader is already running, starts it
 * for the first time if this is the first grant of anything, and clears the
 * GA cookies outright when both purposes end up denied — a `consent update`
 * alone leaves the existing _ga cookie sitting on disk, which is not the
 * same thing as withdrawing consent.
 */
export function applyChoice({ a, p }) {
  if (typeof document === 'undefined') return;
  writeConsent({ a, p });
  recalcOnly();
  const consent = state.effective;
  if (gtagStarted) {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', toGtagConsent(consent));
      if (!consent.ads) {
        window.gtag('set', { ads_data_redaction: true });
      }
    }
  } else if (consent.analytics || consent.ads) {
    startGtag(currentMeasurementId, consent);
  }
  if (!consent.analytics && !consent.ads) {
    clearGaCookies();
  }
  notify();
}

// Exposed for the footer link and the notice page's "Open privacy choices"
// button, which cannot import React state (they render outside/before the
// banner mounts). Set from the runtime, not the component, so it exists even
// on a page where CookieNotice has not rendered yet. No-op during SSR.
if (typeof window !== 'undefined') {
  window.ppConsent = {
    open: openPanel,
    close: closePanel,
    getState,
    applyChoice,
  };
}
